import { Formik } from 'formik';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { AddressElement } from '@stripe/react-stripe-js';
import Sentry from '../../../../../../client/Sentry';
import { BillingAddress, TaxID, TaxIDType } from '../../../../../../typings/Orb.interface';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import { Div } from '../../../../../common/helpers/StyledUtils';
import SelectInput from '../../../../../common/Form/Fields/SelectInput';
import TextInput from '../../../../../common/Form/Fields/TextInput';
import Modal from '../../../../../common/Modal';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import WrapWithStripeElement from '../WrapWithStripeElement';
const TAX_ID_TYPE_OPTIONS: {
  value: TaxIDType | '';
  label: string;
  country: string;
}[] = [{
  value: '',
  label: 'None',
  country: ''
}, {
  value: 'ad_nrt',
  label: 'Andorra NRT',
  country: 'AD'
}, {
  value: 'ae_trn',
  label: 'UAE TRN',
  country: 'AE'
}, {
  value: 'al_tin',
  label: 'Albania TIN',
  country: 'AL'
}, {
  value: 'am_tin',
  label: 'Armenia TIN',
  country: 'AM'
}, {
  value: 'ao_tin',
  label: 'Angola TIN',
  country: 'AO'
}, {
  value: 'ar_cuit',
  label: 'Argentina CUIT',
  country: 'AR'
}, {
  value: 'au_abn',
  label: 'Australia ABN',
  country: 'AU'
}, {
  value: 'au_arn',
  label: 'Australia ARN',
  country: 'AU'
}, {
  value: 'aw_tin',
  label: 'Aruba TIN',
  country: 'AW'
}, {
  value: 'az_tin',
  label: 'Azerbaijan TIN',
  country: 'AZ'
}, {
  value: 'ba_tin',
  label: 'Bosnia and Herzegovina TIN',
  country: 'BA'
}, {
  value: 'bb_tin',
  label: 'Barbados TIN',
  country: 'BB'
}, {
  value: 'bd_bin',
  label: 'Bangladesh BIN',
  country: 'BD'
}, {
  value: 'bf_ifu',
  label: 'Burkina Faso IFU',
  country: 'BF'
}, {
  value: 'bg_uic',
  label: 'Bulgaria UIC',
  country: 'BG'
}, {
  value: 'bh_vat',
  label: 'Bahrain VAT',
  country: 'BH'
}, {
  value: 'bj_ifu',
  label: 'Benin IFU',
  country: 'BJ'
}, {
  value: 'bo_tin',
  label: 'Bolivia TIN',
  country: 'BO'
}, {
  value: 'br_cnpj',
  label: 'Brazil CNPJ',
  country: 'BR'
}, {
  value: 'br_cpf',
  label: 'Brazil CPF',
  country: 'BR'
}, {
  value: 'bs_tin',
  label: 'Bahamas TIN',
  country: 'BS'
}, {
  value: 'by_tin',
  label: 'Belarus TIN',
  country: 'BY'
}, {
  value: 'ca_bn',
  label: 'Canada BN',
  country: 'CA'
}, {
  value: 'ca_gst_hst',
  label: 'Canada GST/HST',
  country: 'CA'
}, {
  value: 'ca_pst_bc',
  label: 'Canada PST (BC)',
  country: 'CA'
}, {
  value: 'ca_pst_mb',
  label: 'Canada PST (MB)',
  country: 'CA'
}, {
  value: 'ca_pst_sk',
  label: 'Canada PST (SK)',
  country: 'CA'
}, {
  value: 'ca_qst',
  label: 'Canada QST',
  country: 'CA'
}, {
  value: 'cd_nif',
  label: 'DR Congo NIF',
  country: 'CD'
}, {
  value: 'ch_uid',
  label: 'Switzerland UID',
  country: 'CH'
}, {
  value: 'ch_vat',
  label: 'Switzerland VAT',
  country: 'CH'
}, {
  value: 'cl_tin',
  label: 'Chile TIN',
  country: 'CL'
}, {
  value: 'cm_niu',
  label: 'Cameroon NIU',
  country: 'CM'
}, {
  value: 'cn_tin',
  label: 'China TIN',
  country: 'CN'
}, {
  value: 'co_nit',
  label: 'Colombia NIT',
  country: 'CO'
}, {
  value: 'cr_tin',
  label: 'Costa Rica TIN',
  country: 'CR'
}, {
  value: 'cv_nif',
  label: 'Cape Verde NIF',
  country: 'CV'
}, {
  value: 'de_stn',
  label: 'Germany STN',
  country: 'DE'
}, {
  value: 'do_rcn',
  label: 'Dominican Republic RCN',
  country: 'DO'
}, {
  value: 'ec_ruc',
  label: 'Ecuador RUC',
  country: 'EC'
}, {
  value: 'eg_tin',
  label: 'Egypt TIN',
  country: 'EG'
}, {
  value: 'es_cif',
  label: 'Spain CIF',
  country: 'ES'
}, {
  value: 'et_tin',
  label: 'Ethiopia TIN',
  country: 'ET'
}, {
  value: 'eu_oss_vat',
  label: 'EU OSS VAT',
  country: 'EU'
}, {
  value: 'eu_vat',
  label: 'EU VAT',
  country: ''
}, {
  value: 'gb_vat',
  label: 'UK VAT',
  country: 'GB'
}, {
  value: 'ge_vat',
  label: 'Georgia VAT',
  country: 'GE'
}, {
  value: 'gn_nif',
  label: 'Guinea NIF',
  country: 'GN'
}, {
  value: 'hk_br',
  label: 'Hong Kong BR',
  country: 'HK'
}, {
  value: 'hr_oib',
  label: 'Croatia OIB',
  country: 'HR'
}, {
  value: 'hu_tin',
  label: 'Hungary TIN',
  country: 'HU'
}, {
  value: 'id_npwp',
  label: 'Indonesia NPWP',
  country: 'ID'
}, {
  value: 'il_vat',
  label: 'Israel VAT',
  country: 'IL'
}, {
  value: 'in_gst',
  label: 'India GST',
  country: 'IN'
}, {
  value: 'is_vat',
  label: 'Iceland VAT',
  country: 'IS'
}, {
  value: 'jp_cn',
  label: 'Japan Corporate Number',
  country: 'JP'
}, {
  value: 'jp_rn',
  label: 'Japan Registered Number',
  country: 'JP'
}, {
  value: 'jp_trn',
  label: 'Japan Tax Registration',
  country: 'JP'
}, {
  value: 'ke_pin',
  label: 'Kenya PIN',
  country: 'KE'
}, {
  value: 'kg_tin',
  label: 'Kyrgyzstan TIN',
  country: 'KG'
}, {
  value: 'kh_tin',
  label: 'Cambodia TIN',
  country: 'KH'
}, {
  value: 'kr_brn',
  label: 'South Korea BRN',
  country: 'KR'
}, {
  value: 'kz_bin',
  label: 'Kazakhstan BIN',
  country: 'KZ'
}, {
  value: 'la_tin',
  label: 'Laos TIN',
  country: 'LA'
}, {
  value: 'li_uid',
  label: 'Liechtenstein UID',
  country: 'LI'
}, {
  value: 'li_vat',
  label: 'Liechtenstein VAT',
  country: 'LI'
}, {
  value: 'ma_vat',
  label: 'Morocco VAT',
  country: 'MA'
}, {
  value: 'md_vat',
  label: 'Moldova VAT',
  country: 'MD'
}, {
  value: 'me_pib',
  label: 'Montenegro PIB',
  country: 'ME'
}, {
  value: 'mk_vat',
  label: 'North Macedonia VAT',
  country: 'MK'
}, {
  value: 'mr_nif',
  label: 'Mauritania NIF',
  country: 'MR'
}, {
  value: 'mx_rfc',
  label: 'Mexico RFC',
  country: 'MX'
}, {
  value: 'my_frp',
  label: 'Malaysia FRP',
  country: 'MY'
}, {
  value: 'my_itn',
  label: 'Malaysia ITN',
  country: 'MY'
}, {
  value: 'my_sst',
  label: 'Malaysia SST',
  country: 'MY'
}, {
  value: 'ng_tin',
  label: 'Nigeria TIN',
  country: 'NG'
}, {
  value: 'no_vat',
  label: 'Norway VAT',
  country: 'NO'
}, {
  value: 'no_voec',
  label: 'Norway VOEC',
  country: 'NO'
}, {
  value: 'np_pan',
  label: 'Nepal PAN',
  country: 'NP'
}, {
  value: 'nz_gst',
  label: 'New Zealand GST',
  country: 'NZ'
}, {
  value: 'om_vat',
  label: 'Oman VAT',
  country: 'OM'
}, {
  value: 'pe_ruc',
  label: 'Peru RUC',
  country: 'PE'
}, {
  value: 'ph_tin',
  label: 'Philippines TIN',
  country: 'PH'
}, {
  value: 'ro_tin',
  label: 'Romania TIN',
  country: 'RO'
}, {
  value: 'rs_pib',
  label: 'Serbia PIB',
  country: 'RS'
}, {
  value: 'ru_inn',
  label: 'Russia INN',
  country: 'RU'
}, {
  value: 'ru_kpp',
  label: 'Russia KPP',
  country: 'RU'
}, {
  value: 'sa_vat',
  label: 'Saudi Arabia VAT',
  country: 'SA'
}, {
  value: 'sg_gst',
  label: 'Singapore GST',
  country: 'SG'
}, {
  value: 'sg_uen',
  label: 'Singapore UEN',
  country: 'SG'
}, {
  value: 'si_tin',
  label: 'Slovenia TIN',
  country: 'SI'
}, {
  value: 'sn_ninea',
  label: 'Senegal NINEA',
  country: 'SN'
}, {
  value: 'sr_fin',
  label: 'Suriname FIN',
  country: 'SR'
}, {
  value: 'sv_nit',
  label: 'El Salvador NIT',
  country: 'SV'
}, {
  value: 'th_vat',
  label: 'Thailand VAT',
  country: 'TH'
}, {
  value: 'tj_tin',
  label: 'Tajikistan TIN',
  country: 'TJ'
}, {
  value: 'tr_tin',
  label: 'Turkey TIN',
  country: 'TR'
}, {
  value: 'tw_vat',
  label: 'Taiwan VAT',
  country: 'TW'
}, {
  value: 'tz_vat',
  label: 'Tanzania VAT',
  country: 'TZ'
}, {
  value: 'ua_vat',
  label: 'Ukraine VAT',
  country: 'UA'
}, {
  value: 'ug_tin',
  label: 'Uganda TIN',
  country: 'UG'
}, {
  value: 'us_ein',
  label: 'United States EIN',
  country: 'US'
}, {
  value: 'uy_ruc',
  label: 'Uruguay RUC',
  country: 'UY'
}, {
  value: 'uz_tin',
  label: 'Uzbekistan TIN',
  country: 'UZ'
}, {
  value: 'uz_vat',
  label: 'Uzbekistan VAT',
  country: 'UZ'
}, {
  value: 've_rif',
  label: 'Venezuela RIF',
  country: 'VE'
}, {
  value: 'vn_tin',
  label: 'Vietnam TIN',
  country: 'VN'
}, {
  value: 'za_vat',
  label: 'South Africa VAT',
  country: 'ZA'
}, {
  value: 'zm_tin',
  label: 'Zambia TIN',
  country: 'ZM'
}, {
  value: 'zw_tin',
  label: 'Zimbabwe TIN',
  country: 'ZW'
}];
interface BillingAddressModalProps {
  billing_address: BillingAddress | null;
  tax_id: TaxID | null;
  mutate: (address: BillingAddress, tax_id: TaxID | null) => void;
}
const BillingAddressModal: React.FC<BillingAddressModalProps> = ({
  billing_address,
  tax_id,
  mutate
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const history = useHistory();
  const onClose = () => history.push({
    pathname: '/settings/organization/billing',
    state: {
      scroll: false
    }
  });
  const handleSubmit = async (elements, form_values: {
    tax_id_type: TaxIDType | '';
    tax_id_value: string;
  }) => {
    try {
      const address_element = elements.getElement(AddressElement);
      if (address_element == null) {
        throw new Error('Address element unavailable.');
      }
      const {
        complete,
        value
      } = await address_element.getValue();
      if (!complete) {
        throw new Error('Please complete all required address fields.');
      }
      const address_data: BillingAddress = {
        name: value.name || null,
        line1: value.address.line1 || null,
        line2: value.address.line2 || null,
        city: value.address.city || null,
        state: value.address.state || null,
        postal_code: value.address.postal_code || null,
        country: value.address.country || null
      };
      let tax_id_data: TaxID | null = null;
      if (form_values.tax_id_type && form_values.tax_id_value) {
        const tax_option = TAX_ID_TYPE_OPTIONS.find(o => o.value === form_values.tax_id_type);
        const country = tax_option?.country || value.address.country as TaxID['country'] || 'US';
        tax_id_data = {
          country: country as TaxID['country'],
          type: form_values.tax_id_type,
          value: form_values.tax_id_value
        };
      }
      await HookdeckAPI.billing.updateBillingAddress(address_data, tax_id_data);
      mutate(address_data, tax_id_data);
      addToast('success', 'Billing address updated.');
      history.push('/settings/organization/billing');
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      addToast('error', err instanceof Error ? err.message : 'Something went wrong, please try again.');
    }
  };
  const modal_props = {
    title: 'Update billing address',
    cancel_label: 'Cancel',
    submit_label: 'Confirm',
    onCancel: onClose,
    onClose: onClose,
    hide_close: true
  };
  const default_values = {
    name: billing_address?.name || '',
    address: {
      line1: billing_address?.line1 || '',
      line2: billing_address?.line2 || '',
      city: billing_address?.city || '',
      state: billing_address?.state || '',
      postal_code: billing_address?.postal_code || '',
      country: billing_address?.country || 'US'
    }
  };
  return <WrapWithStripeElement loading={<Modal {...modal_props}>
          <Div flex={{
      align: 'center',
      justify: 'center'
    }} p={{
      t: 8,
      y: 6
    }}>
            <Loading />
          </Div>
        </Modal>}>
      {({
      elements: elements_0
    }) => <Formik initialValues={{
      address_complete: !!billing_address?.line1,
      tax_id_type: (tax_id?.type || '') as TaxIDType | '',
      tax_id_value: tax_id?.value || ''
    }} onSubmit={values => handleSubmit(elements_0, {
      tax_id_type: values.tax_id_type,
      tax_id_value: values.tax_id_value
    })} validate={values_0 => {
      const errors: {
        address_complete?: string;
        tax_id_value?: string;
      } = {};
      if (!values_0.address_complete) {
        errors.address_complete = 'Complete address is required';
      }
      if (values_0.tax_id_type && !values_0.tax_id_value) {
        errors.tax_id_value = 'Tax ID value is required when type is selected';
      }
      return errors;
    }}>
          {props => <form onSubmit={props.handleSubmit}>
              <Modal {...modal_props} is_submitting={props.isSubmitting} footer_content={props.errors.address_complete && props.touched.address_complete && <Text m={{
          t: 1
        }} danger>
                      {props.errors.address_complete}
                    </Text>}>
                <Div m={{
            b: 4
          }}>
                  <AddressElement options={{
              mode: 'billing',
              defaultValues: default_values,
              display: {
                name: 'organization'
              }
            }} onChange={event => props.setFieldValue('address_complete', event.complete)} />
                </Div>
                <Div m={{
            t: 4
          }}>
                  <Text size="xs" semi muted m={{
              b: 2
            }}>
                    Tax ID (optional)
                  </Text>
                  <Div grid={{
              columns: 2,
              gap: 4
            }}>
                    <SelectInput name="tax_id_type" block placeholder="Select tax ID type" options={TAX_ID_TYPE_OPTIONS.map(o_0 => ({
                value: o_0.value,
                label: o_0.label
              }))} m={{
                b: 0
              }} />
                    <TextInput name="tax_id_value" placeholder="Tax ID number" disabled={!props.values.tax_id_type} m={{
                b: 0
              }} />
                  </Div>
                </Div>
              </Modal>
            </form>}
        </Formik>}
    </WrapWithStripeElement>;
};
export default BillingAddressModal;